import { LoadDataType } from 'klinecharts'
import type { Chart, KLineData } from 'klinecharts'
import type { DatafeedSocket, RawCandleRow } from '@/bot/adapters/datafeedSocket'
import type { CandleData } from '@/bot/types/chartData'
import type { FormingBarState } from '@/bot/utils/formingBarBuffer'
import type { ExtendResult } from '@/bot/hooks/useGraphCandleWindow'
import { binarySearchFirstAfter } from '@/bot/hooks/usePlayback'
import {
  closedIndexRange,
  getIndexRange,
  getVisibleBarCount,
  setIndexRange,
  type IndexRange,
} from '@/bot/components/klineViewport'
import { getTimeframeSeconds } from '@/utils/chartUtils'
import { nextHeikinAshi, toHeikinAshi } from './heikinAshi'
import type { WorkbenchChartStatus } from './WorkbenchChart'

/** Bars the canvas opens with, ending at the anchor. */
export const INITIAL_BARS = 800
/** Bars per history page, either direction. */
export const PAGE_BARS = 500
/** Bars fetched past a replay cursor when the series is reloaded around it. */
const AHEAD_BARS = 300
/**
 * Ceiling on bars buffered past the cutoff. usePlayback prefetches whenever the cursor is past
 * 80% of its candles, and with a long left history that is true on every tick — without a cap
 * the buffer would grow by a page per bar.
 */
const MAX_AHEAD_BARS = 2 * PAGE_BARS
/** A forward reveal of at most this many bars is appended bar by bar; more is one re-apply. */
const MAX_APPEND = 16
/** Market gaps (weekends, holidays) make a bar count's wall-clock span unpredictable. */
const GAP_SLACK = 2
/** Empty right-hand pages tolerated (each 4x wider) before the data is called exhausted. */
const RIGHT_PROBES = 4

export type CutoffFrame = 'follow' | 'edge'

// Replay zoom, px per bar.
export const REPLAY_ZOOM_FACTOR = 1.5
export const REPLAY_MAX_BAR_SPACE = 18

export type CanvasSeriesEvent =
  | { type: 'status'; status: WorkbenchChartStatus }
  | { type: 'data' }

type LoadDataParams = Parameters<Parameters<Chart['setLoadDataCallback']>[0]>[0]

function toKline(rows: RawCandleRow[]): KLineData[] {
  return rows.map(([time, open, high, low, close, volume]) => ({
    timestamp: time * 1000,
    open,
    high,
    low,
    close,
    volume,
  }))
}

function toCandle(b: KLineData): CandleData {
  return { time: Math.floor(b.timestamp / 1000), open: b.open, high: b.high, low: b.low, close: b.close, volume: b.volume }
}

/**
 * The canvas chart's candle series: the ONE writer of the engine's data list.
 *
 * It owns what WorkbenchChart used to do inline — the initial window, pan-left pagination and
 * the Heikin Ashi transform — and adds what the walkthrough's bar replay needs on the same
 * chart the user draws on:
 *
 *  - a CUTOFF. With one set, the engine holds only the bars at or before it (plus, while the
 *    replay animates, the one bar forming after it); the rest of what was loaded stays buffered
 *    here. The engine's list is therefore always a PREFIX of `raw`, so an engine index IS a raw
 *    index — which is what lets the replay's index maths (usePlayback, nextFollow) run unchanged,
 *    and why a history page can prepend to both.
 *  - lookahead (`extendRight`) and a reload around a moment (`loadAround`), for a cursor the
 *    loaded window does not reach;
 *  - right-side pagination, so that after a reload in the past the user can still pan back to
 *    the present once the cutoff lifts.
 *
 * Every engine quirk it relies on is noted where it is used. Two are global and easy to trip:
 * one `_loading` flag serves BOTH paging directions, so every load-data request is answered
 * promptly (stale ones included); and `applyNewData` re-applies the last right offset anyone
 * wrote, so a live-edge load after a replay first writes the canvas's own offset back.
 */
/** A window reload (`loadAround`): which bars it fetches, and whether it was called off. */
interface AroundRequest {
  ts: number
  symbol: string
  timeframe: string
  cancelled: boolean
  promise: Promise<void>
}

export class CanvasSeries {
  private readonly chart: Chart
  private readonly socket: DatafeedSocket | null
  private readonly listeners = new Set<(e: CanvasSeriesEvent) => void>()
  private version = 0

  private symbol = ''
  private timeframe = ''
  private anchorTs: number | null = null
  private ha: boolean

  /** Every loaded bar, raw (untransformed), oldest first. */
  private raw: KLineData[] = []
  /** `raw` in unix seconds, rebuilt (new identity) on every change. */
  private candles: CandleData[] = []
  /** Closed bars currently in the engine: always `raw.slice(0, applied)`. */
  private applied = 0
  private cutoff: number | null = null
  /** The symbol the cutoff was set for. A cutoff only ever applies to that symbol's bars. */
  private cutoffSymbol: string | null = null
  /** Blank bars kept right of the newest revealed bar while pinned — the replay's margin. */
  private padBars = 0
  /** ms timestamp of the forming bar in the engine, or null. Always `raw[applied]`'s. */
  private formingTs: number | null = null
  private lastFormingRaw: FormingBarState | null = null
  private forwardMore = false
  /** Nothing newer than `raw`'s last bar exists (or is allowed: the anchor). */
  private rightExhausted = true
  /** Load epoch: bumped by every (re)load, so a late answer for an older window is dropped. */
  private seq = 0
  private inFlightRight = false
  /** The window request in flight — keyed by the bars it fetches, not just the moment. */
  private pendingAround: AroundRequest | null = null
  /** The canvas's own right margin, captured before any data; the user can move it. */
  private homeOffsetPx: number
  /** The user's gap after the newest bar, px, for the canvas and the replay alike; null until set. */
  private rightGapPx: number | null = null
  /** Whether the replay has written the engine's stored right offset since the last home reset. */
  private offsetDirty = false
  /** Set while the replay zoom is on. */
  private userBarSpace: number | null = null
  private replayBarSpace: number | null = null

  constructor(opts: { chart: Chart; socket: DatafeedSocket | null; heikinAshi: boolean }) {
    this.chart = opts.chart
    this.socket = opts.socket
    this.ha = opts.heikinAshi
    this.homeOffsetPx = opts.chart.getOffsetRightDistance()
  }

  // ---- Subscription (useSyncExternalStore-compatible) -----------------------------------

  subscribe = (fn: (e: CanvasSeriesEvent) => void): (() => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  getVersion = (): number => this.version

  private emit(e: CanvasSeriesEvent) {
    if (e.type === 'data') this.version += 1
    for (const fn of this.listeners) fn(e)
  }

  private emitStatus(status: Omit<WorkbenchChartStatus, 'symbol'>) {
    this.emit({ type: 'status', status: { ...status, symbol: this.symbol } })
  }

  // ---- Reads -------------------------------------------------------------------------------

  /** Every loaded bar, including those buffered past the cutoff. unix seconds. */
  getCandles(): CandleData[] {
    return this.candles
  }

  /** Closed bars in the engine — the reveal count while a cutoff is set. */
  getAppliedCount(): number {
    return this.applied
  }

  /** The newest closed bar in the engine, unix seconds. */
  getAppliedLastTs(): number | null {
    return this.applied > 0 ? this.candles[this.applied - 1].time : null
  }

  getCutoff(): number | null {
    return this.cutoff
  }

  /** Whether the loaded window runs all the way to the anchor (nothing newer to page in). */
  reachesAnchor(): boolean {
    return this.rightExhausted
  }

  /** Whether `ts` sits inside what is loaded, so a cutoff there needs no fetch. */
  covers(ts: number): boolean {
    if (this.candles.length === 0) return false
    const last = this.candles[this.candles.length - 1].time
    return this.candles[0].time <= ts && (ts <= last || this.rightExhausted)
  }

  getIndexRange(): IndexRange | null {
    return closedIndexRange(this.chart, this.applied)
  }

  getVisibleBarCount(): number | null {
    return getVisibleBarCount(this.chart)
  }

  getBarSpace(): number {
    return this.chart.getBarSpace()
  }

  /** Zoom unchanged by the user. */
  private atReplayZoom(): boolean {
    return this.replayBarSpace !== null && Math.abs(this.chart.getBarSpace() - this.replayBarSpace) < 1e-6
  }

  /** Zooms in once, never out. */
  zoomInForReplay(): void {
    if (this.userBarSpace !== null) return
    const user = this.chart.getBarSpace()
    if (!(user > 0)) return
    const target = Math.max(user, Math.min(REPLAY_MAX_BAR_SPACE, Math.round(user * REPLAY_ZOOM_FACTOR)))
    this.userBarSpace = user
    this.replayBarSpace = target
    if (target !== user) this.chart.setBarSpace(target)
  }

  /** Back to the user's zoom, unless they changed it. */
  restoreUserZoom(): void {
    if (this.userBarSpace === null) return
    if (this.atReplayZoom()) this.chart.setBarSpace(this.userBarSpace)
    this.userBarSpace = null
    this.replayBarSpace = null
  }

  /** Applies a saved zoom. */
  setUserZoom(barSpace: number): void {
    if (this.userBarSpace === null) {
      this.chart.setBarSpace(barSpace)
      return
    }
    this.userBarSpace = null
    this.replayBarSpace = null
    this.chart.setBarSpace(barSpace)
    this.zoomInForReplay()
  }

  /** The user's zoom, for saving. */
  getSavedBarSpace(): number {
    return this.atReplayZoom() ? this.userBarSpace! : this.chart.getBarSpace()
  }

  /**
   * Show `range` (closed-bar indices) at the chart's current zoom. Writes through
   * setOffsetRightDistance, so it works while a drawing tool holds the scroll lock.
   */
  setIndexRange(range: IndexRange, rightPadBars: number): void {
    if (this.applied === 0) return
    this.offsetDirty = true
    setIndexRange(this.chart, range, this.applied, rightPadBars, { keepZoom: true })
  }

  /** The user's gap after the newest bar, px: the home margin, and the replay's while it cuts. */
  setRightGap(px: number): void {
    this.rightGapPx = px
    this.homeOffsetPx = px
    if (this.cutoff !== null) {
      const barSpace = this.chart.getBarSpace()
      // In the replay's own terms (useCanvasReplay): bars, plus setIndexRange's half bar.
      if (barSpace > 0) this.padBars = px / barSpace + 0.5
    } else {
      this.offsetDirty = false
    }
    this.chart.setOffsetRightDistance(px)
  }

  getRightGap(): number | null {
    return this.rightGapPx
  }

  /**
   * "Reset chart view": the newest bar, at the canvas's own right margin. The engine's
   * scrollToRealTime goes back to the last right offset anyone WROTE, and the replay writes its
   * own (negative ones, past the playhead) — so once a replay has run, the home margin is put
   * back first. Not while a cutoff holds: then the replay's edge is the one to return to.
   */
  scrollToLatest(): void {
    if (this.cutoff === null) this.resetHomeOffset()
    this.chart.scrollToRealTime()
  }

  // ---- Canvas lifecycle --------------------------------------------------------------------

  /** Load (or reload) for the canvas's symbol / timeframe / anchor. */
  async load(p: { symbol: string; timeframe: string; anchorTs: number | null }): Promise<void> {
    const symbolChanged = p.symbol !== this.symbol
    const timeframeChanged = p.timeframe !== this.timeframe
    this.symbol = p.symbol
    this.timeframe = p.timeframe
    this.anchorTs = p.anchorTs
    // A cutoff belongs to the symbol it was set for — which may be the one arriving now: the
    // replay's layout effect can set it a beat before this passive load runs.
    if (this.cutoff !== null && this.cutoffSymbol !== p.symbol) this.dropCutoff()
    const socket = this.socket
    if (!socket || !p.symbol) return

    if (this.cutoff !== null) {
      // Only the anchor moved (a symbol's meta arriving): the replay's window is still right.
      if (!symbolChanged && !timeframeChanged && this.covers(this.cutoff)) return
      return this.loadAround(this.cutoff)
    }

    const seq = ++this.seq
    this.pendingAround = null
    this.emitStatus({ loading: true, error: null, empty: false })
    try {
      const rows = await socket.requestHistory({
        symbol: p.symbol,
        timeframe: p.timeframe,
        to: this.limitSec(),
        count: INITIAL_BARS,
      })
      if (seq !== this.seq) return // superseded
      this.setRaw(toKline(rows))
      this.forwardMore = rows.length > 0
      // The window ends at the anchor, and nothing past the anchor is ever shown.
      this.rightExhausted = true
      this.formingTs = null
      if (this.cutoff !== null && this.cutoffSymbol === p.symbol) {
        // A replay set its cutoff while this load was in flight — before there were bars (so
        // setCutoff only stored it) or over the old window. The new window shows up to it,
        // never every bar: the replay sees its cutoff already set and would not re-assert it.
        this.lastFormingRaw = null
        this.applied = 0
        this.reapply(binarySearchFirstAfter(this.candles, this.cutoff), 'edge')
        this.emitStatus({ loading: false, error: null, empty: rows.length === 0 })
        if (!this.covers(this.cutoff)) void this.loadAround(this.cutoff)
        return
      }
      this.applied = this.raw.length
      this.resetHomeOffset()
      // `more: true` lets a left-scroll trigger the load callback.
      this.chart.applyNewData(this.display(this.applied), this.forwardMore)
      this.emitStatus({ loading: false, error: null, empty: rows.length === 0 })
      this.emit({ type: 'data' })
    } catch (err) {
      if (seq !== this.seq) return
      this.setRaw([])
      this.applied = 0
      this.formingTs = null
      this.chart.applyNewData([], false)
      this.emitStatus({ loading: false, error: err instanceof Error ? err.message : 'Failed to load candles', empty: true })
      this.emit({ type: 'data' })
    }
  }

  /**
   * Chart type flip. Only Heikin Ashi changes the bars; the caller skips other flips.
   *
   * Without a cutoff this is the path the canvas has always had: re-apply, and when scrolled
   * back re-anchor the last visible bar (the fractional -1.5 is the engine's rounding fixed
   * point, so repeated flips don't ratchet the view). With one, the prefix is re-applied and
   * held through setIndexRange, which works under a drawing tool's scroll lock.
   */
  setHeikinAshi(on: boolean): void {
    if (on === this.ha) return
    this.ha = on
    if (this.raw.length === 0) return
    if (this.cutoff !== null) {
      const forming = this.lastFormingRaw
      this.reapply(this.applied, 'follow')
      if (forming) this.setFormingBar(forming)
      return
    }
    const chart = this.chart
    // vr.to < bar count ⇔ scrolled back into history. At the live edge the reset
    // applyNewData performs already reproduces the prior view (default margin).
    const vr = chart.getVisibleRange()
    const scrolledBack = vr.to < chart.getDataList().length
    if (!scrolledBack) this.resetHomeOffset()
    chart.applyNewData(this.display(this.raw.length), this.forwardMore)
    if (scrolledBack) {
      // scrolling is a no-op while a drawing tool holds the scroll lock.
      const scrollLocked = !chart.isScrollEnabled()
      if (scrollLocked) chart.setScrollEnabled(true)
      chart.scrollToDataIndex(Math.max(0, vr.to - 1.5))
      if (scrollLocked) chart.setScrollEnabled(false)
    }
    this.emit({ type: 'data' })
  }

  dispose(): void {
    this.seq += 1
    this.listeners.clear()
  }

  // ---- Replay ------------------------------------------------------------------------------

  /**
   * Reveal up to `ts` (unix seconds), or everything with null.
   *
   * `frame` 'edge' pins the cutoff bar to the right edge (first engagement, a restart). 'follow'
   * keeps whatever the user is looking at: a pinned view stays pinned, a scrolled-back one
   * stays put. `rightPadBars` is the blank margin kept right of the newest bar when pinned.
   */
  setCutoff(
    ts: number | null,
    frame: CutoffFrame = 'follow',
    rightPadBars = 0,
    symbol: string = this.symbol,
  ): void {
    this.padBars = rightPadBars
    if (ts === null) {
      if (this.cutoff === null) return
      this.dropCutoff()
      this.lift()
      return
    }
    const wasCut = this.cutoff !== null
    this.cutoff = ts
    this.cutoffSymbol = symbol
    // Set for a symbol the canvas has not loaded yet: load() applies it when that symbol lands.
    if (symbol !== this.symbol) return
    if (this.candles.length === 0) return
    const n = binarySearchFirstAfter(this.candles, ts)

    if (wasCut && frame === 'follow') {
      if (n === this.applied) return
      if (n > this.applied && n - this.applied <= MAX_APPEND && this.engineMatches()) {
        this.appendRevealed(n)
        return
      }
    }
    this.reapply(n, wasCut ? frame : 'edge', rightPadBars)
    if (!this.covers(ts)) void this.loadAround(ts)
  }

  /**
   * Replace the loaded window with one around `ts`: INITIAL_BARS up to it, and a page of
   * lookahead after it. A concurrent call for the same moment shares the request.
   */
  loadAround(ts: number): Promise<void> {
    const { symbol, timeframe } = this
    const pending = this.pendingAround
    // Shared only for the same bars: one sent before a symbol or timeframe change fetches the
    // old ones, and would land under the new timeframe (it was never superseded).
    if (pending && pending.ts === ts && pending.symbol === symbol && pending.timeframe === timeframe) {
      return pending.promise
    }
    const req: AroundRequest = { ts, symbol, timeframe, cancelled: false, promise: Promise.resolve() }
    this.pendingAround = req
    req.promise = this.doLoadAround(req)
    return req.promise
  }

  /**
   * Drop the window reload in flight, if any. For a replay that went away without finishing (a
   * hide, the run replaced): landing late, its window would swap the canvas back to the replay's
   * bars and scroll there. Its own request only — paging in flight is left alone.
   */
  cancelWindowLoad(): void {
    const req = this.pendingAround
    if (!req) return
    req.cancelled = true
    this.pendingAround = null
    this.emitStatus({ loading: false, error: null, empty: this.raw.length === 0 })
  }

  private async doLoadAround(req: AroundRequest): Promise<void> {
    const { ts, symbol, timeframe } = req
    const socket = this.socket
    if (!socket || !symbol) {
      if (this.pendingAround === req) this.pendingAround = null
      return
    }
    const seq = ++this.seq
    const limit = this.limitSec()
    const aheadTo = Math.min(limit, ts + AHEAD_BARS * getTimeframeSeconds(timeframe) * GAP_SLACK)
    this.emitStatus({ loading: true, error: null, empty: false })
    try {
      const [before, after] = await Promise.all([
        socket.requestHistory({ symbol, timeframe, to: ts, count: INITIAL_BARS }),
        aheadTo > ts + 1
          ? socket.requestHistory({ symbol, timeframe, from: ts + 1, to: aheadTo })
          : Promise.resolve([] as RawCandleRow[]),
      ])
      if (seq !== this.seq || req.cancelled) return
      const history = toKline(before)
      const lastMs = history.length > 0 ? history[history.length - 1].timestamp : -Infinity
      // The datafeed answers from the bar CONTAINING `from`, so the lookahead repeats the bar
      // at the cursor. Keep strictly newer bars only.
      const ahead = toKline(after).filter((b) => b.timestamp > lastMs)
      this.setRaw([...history, ...ahead])
      this.forwardMore = before.length > 0
      this.rightExhausted = aheadTo >= limit && ahead.length === 0
      // The engine still holds the old window; nothing about its layout carries over.
      this.formingTs = null
      this.lastFormingRaw = null
      if (this.cutoff !== null) {
        this.applied = 0
        this.reapply(binarySearchFirstAfter(this.candles, this.cutoff), 'edge')
      } else {
        this.applied = this.raw.length
        this.chart.applyNewData(this.display(this.applied), this.forwardMore)
        const idx = binarySearchFirstAfter(this.candles, ts) - 1
        if (idx >= 0) this.setIndexRange({ from: idx, to: idx }, 0)
        this.emit({ type: 'data' })
      }
      this.emitStatus({ loading: false, error: null, empty: this.raw.length === 0 })
    } catch (err) {
      if (seq !== this.seq || req.cancelled) return
      this.emitStatus({ loading: false, error: err instanceof Error ? err.message : 'Failed to load candles', empty: this.raw.length === 0 })
    } finally {
      if (this.pendingAround === req) this.pendingAround = null
    }
  }

  /**
   * Make sure history reaches back to `ts` — a trade row asking to be shown can sit in the
   * run's warm-up, older than anything the user has paged in. One range request for the gap,
   * prepended like a history page; the view is held on the same bars.
   */
  async ensureFrom(ts: number): Promise<void> {
    const socket = this.socket
    if (!socket || this.raw.length === 0 || this.candles[0].time <= ts) return
    const seq = this.seq
    const firstMs = this.raw[0].timestamp
    // A few bars of lead so the bar at `ts` is not the chart's very first.
    const from = ts - 50 * getTimeframeSeconds(this.timeframe)
    try {
      const rows = await socket.requestHistory({
        symbol: this.symbol,
        timeframe: this.timeframe,
        from,
        to: Math.floor(firstMs / 1000) - 1,
      })
      if (seq !== this.seq || this.raw[0]?.timestamp !== firstMs) return
      const bars = toKline(rows).filter((b) => b.timestamp < firstMs)
      if (bars.length === 0) return
      const chart = this.chart
      const prev = this.applied > 0 ? closedIndexRange(chart, this.applied) : null
      this.setRaw([...bars, ...this.raw])
      this.applied += bars.length
      this.formingTs = null
      this.lastFormingRaw = null
      chart.applyNewData(this.display(this.applied), this.forwardMore)
      if (prev) {
        this.offsetDirty = true
        setIndexRange(
          chart,
          { from: prev.from + bars.length, to: prev.to + bars.length },
          this.applied,
          prev.to >= this.applied - bars.length - 1 ? this.padBars : 0,
          { keepZoom: true },
        )
      }
      this.emit({ type: 'data' })
    } catch {
      /* the row just cannot be shown; nothing else depends on it */
    }
  }

  /**
   * Fetch the next bars after what is loaded, into the buffer.
   *
   * 'skipped' while a fetch is in flight, once enough is buffered past the cutoff, or when the
   * window was reloaded underneath the request; 'exhausted' when nothing newer exists (up to
   * the anchor). With no cutoff, the new bars also reach the engine.
   */
  async extendRight(): Promise<ExtendResult> {
    if (this.inFlightRight || this.raw.length === 0 || !this.socket) return 'skipped'
    if (this.cutoff !== null && this.raw.length - this.applied >= MAX_AHEAD_BARS) return 'skipped'
    if (this.rightExhausted) return 'exhausted'
    this.inFlightRight = true
    const seq = this.seq
    try {
      const fresh = await this.fetchAfter()
      if (seq !== this.seq) return 'skipped'
      if (fresh.length === 0) {
        this.rightExhausted = true
        return 'exhausted'
      }
      this.appendRaw(fresh)
      return 'grew'
    } catch {
      return 'skipped'
    } finally {
      this.inFlightRight = false
    }
  }

  /**
   * Paint (or clear) the bar forming right after the cutoff.
   *
   * Only ever the slot `raw[applied]`, and only while a cutoff is set: the forming bar is the
   * replay's, and it is always the next bar the cutoff will reveal. The reveal itself then
   * overwrites it in place, because the real bar carries the same timestamp.
   */
  setFormingBar(bar: FormingBarState | null): void {
    const chart = this.chart
    if (bar === null) {
      this.lastFormingRaw = null
      if (this.formingTs === null) return
      this.reapply(this.applied, 'follow')
      return
    }
    if (this.cutoff === null) return
    const slot = this.raw[this.applied]
    if (!slot || bar.time * 1000 !== slot.timestamp) return
    const list = chart.getDataList()
    const engineLastMs = list.length > 0 ? list[list.length - 1].timestamp : null
    // Older than the engine's last bar is a silent no-op inside updateData.
    if (engineLastMs !== null && slot.timestamp < engineLastMs) return
    const k: KLineData = {
      timestamp: slot.timestamp,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
      volume: bar.volume,
    }
    const newSlot = this.formingTs === null
    chart.updateData(this.ha ? nextHeikinAshi(list[this.applied - 1], k) : k)
    this.formingTs = slot.timestamp
    this.lastFormingRaw = bar

    // Keep the forming bar on screen. With the replay's right margin this is a no-op; after a
    // reload or a user zoom that ate the margin the appended bar lands off-screen — so nudge one
    // bar right, but only when the view is pinned to the last closed bar.
    if (newSlot) {
      const vr = getIndexRange(chart)
      if (vr && vr.to === this.applied - 1) {
        this.offsetDirty = true
        setIndexRange(
          chart,
          { from: Math.max(0, this.applied - (vr.to - vr.from)), to: this.applied },
          this.applied + 1,
          0,
          { keepZoom: true },
        )
      }
    }
  }

  // ---- Paging (klinecharts load-data callback) ---------------------------------------------

  readonly handleLoadData = (params: LoadDataParams): void => {
    if (params.type === LoadDataType.Forward) {
      this.pageForward(params)
      return
    }
    if (params.type === LoadDataType.Backward) {
      this.pageBackward(params)
      return
    }
    params.callback([], false)
  }

  /** Older history, when the user scrolls to the left edge. Allowed during a replay. */
  private pageForward({ data, callback }: LoadDataParams): void {
    const socket = this.socket
    if (!data?.timestamp || !socket || !this.symbol) {
      callback([], false)
      return
    }
    const seq = this.seq
    const firstMs = this.raw[0]?.timestamp
    socket
      .requestHistory({
        symbol: this.symbol,
        timeframe: this.timeframe,
        to: Math.floor(data.timestamp / 1000) - 1,
        count: PAGE_BARS,
      })
      .then((rows) => {
        // A late page for another window: answer (the engine's one loading flag blocks the
        // other direction until we do) and keep paging armed, but prepend nothing.
        if (seq !== this.seq || this.raw[0]?.timestamp !== firstMs) {
          callback([], this.forwardMore)
          return
        }
        const bars = toKline(rows).filter((b) => firstMs === undefined || b.timestamp < firstMs)
        this.setRaw([...bars, ...this.raw])
        this.applied += bars.length
        // On what was actually prepended: a page that only overlapped would otherwise keep
        // asking for the same bars forever.
        this.forwardMore = bars.length > 0
        // Note: in Heikin Ashi mode each older chunk is transformed on its own (seeded from its
        // first raw candle) — see heikinAshi.ts for the seam caveat.
        callback(this.ha ? toHeikinAshi(bars) : bars, this.forwardMore)
        this.emit({ type: 'data' })
      })
      .catch(() => callback([], false))
  }

  /**
   * Newer data, when the user scrolls to the right edge.
   *
   * The engine asks on EVERY adjust at the live edge — each appended replay bar included — so
   * the common answers are immediate: a set cutoff owns the right edge, and a window that
   * already reaches the anchor has nothing newer. Only a window reloaded in the past pages here.
   */
  private pageBackward({ callback }: LoadDataParams): void {
    if (this.cutoff !== null || this.rightExhausted || this.raw.length === 0 || !this.socket) {
      callback([], false)
      return
    }
    const seq = this.seq
    const lastMs = this.raw[this.raw.length - 1].timestamp
    this.fetchAfter()
      .then((fresh) => {
        if (seq !== this.seq || this.raw[this.raw.length - 1]?.timestamp !== lastMs) {
          callback([], !this.rightExhausted)
          return
        }
        if (fresh.length === 0) {
          this.rightExhausted = true
          callback([], false)
          return
        }
        if (this.cutoff !== null) {
          // A replay engaged while the page was in flight: keep the bars as buffer.
          this.setRaw([...this.raw, ...fresh])
          callback([], false)
          this.emit({ type: 'data' })
          return
        }
        const chart = this.chart
        const prev = chart.getDataList()
        const shown = this.ha ? this.chainHeikinAshi(prev[prev.length - 1], fresh) : fresh
        this.setRaw([...this.raw, ...fresh])
        this.applied = this.raw.length
        callback(shown, true)
        this.holdAfterAppend(shown.length)
        this.emit({ type: 'data' })
      })
      .catch(() => callback([], false))
  }

  // ---- Internals ---------------------------------------------------------------------------

  /**
   * Put the view back on the bars it showed before `k` bars were appended to the engine.
   *
   * The engine's Backward append concatenates without touching its right offset, which is
   * measured from the LAST bar — so the view rides along with the new end and jumps `k` bars
   * ahead. That is what used to throw the user a page forward the moment the replay's cutoff
   * lifted (the lift's re-apply asks for the next page). scroll(d) sets the offset to its value
   * before minus d / barSpace: an exact inverse, with no offset to read (the getter clamps at 0).
   * Same task as the append, so the jump is never painted.
   */
  private holdAfterAppend(k: number) {
    if (k <= 0) return
    const chart = this.chart
    // Scrolling is a no-op while a drawing tool holds the scroll lock.
    const scrollLocked = !chart.isScrollEnabled()
    if (scrollLocked) chart.setScrollEnabled(true)
    chart.scrollByDistance(k * chart.getBarSpace())
    if (scrollLocked) chart.setScrollEnabled(false)
  }

  /** The newest bar the canvas may ever show: the anchor (the symbol's data end), or now. */
  private limitSec(): number {
    return this.anchorTs ?? Math.floor(Date.now() / 1000)
  }

  private setRaw(raw: KLineData[]) {
    this.raw = raw
    this.candles = raw.map(toCandle)
  }

  private dropCutoff() {
    this.cutoff = null
    this.cutoffSymbol = null
    this.formingTs = null
    this.lastFormingRaw = null
    this.pendingAround = null
  }

  /** The engine-ready bars for `raw.slice(0, n)`. HA is causal, so a prefix transforms alone. */
  private display(n: number): KLineData[] {
    const bars = n >= this.raw.length ? this.raw : this.raw.slice(0, n)
    return this.ha ? toHeikinAshi(bars) : bars
  }

  private chainHeikinAshi(prev: KLineData | undefined, bars: KLineData[]): KLineData[] {
    const out: KLineData[] = []
    let p = prev
    for (const b of bars) {
      const h = nextHeikinAshi(p, b)
      out.push(h)
      p = h
    }
    return out
  }

  /** The engine holds exactly our closed prefix, plus at most the forming bar in its slot. */
  private engineMatches(): boolean {
    const list = this.chart.getDataList()
    const expected = this.applied + (this.formingTs !== null ? 1 : 0)
    if (list.length !== expected) return false
    if (this.applied > 0 && list[0]?.timestamp !== this.raw[0]?.timestamp) return false
    return this.formingTs === null || this.formingTs === this.raw[this.applied]?.timestamp
  }

  /** The fast path: one updateData per newly revealed bar, the replay's steady state. */
  private appendRevealed(n: number) {
    const chart = this.chart
    for (let i = this.applied; i < n; i++) {
      if (this.ha) {
        const list = chart.getDataList()
        chart.updateData(nextHeikinAshi(list[i - 1], this.raw[i]))
      } else {
        chart.updateData(this.raw[i])
      }
    }
    // The first write carried the forming bar's timestamp and overwrote it in place.
    this.formingTs = null
    this.lastFormingRaw = null
    this.applied = n
    this.emit({ type: 'data' })
  }

  /**
   * Re-apply `raw.slice(0, n)` wholesale — a move back, a big jump, a reload — and put the view
   * back: at the edge for 'edge' (or when it was pinned there, or past the new end), otherwise
   * on the same bars. applyNewData resets the offset, so the view has to be restored by hand.
   */
  private reapply(n: number, frame: CutoffFrame, rightPadBars = this.padBars) {
    const chart = this.chart
    const prev = this.applied > 0 ? closedIndexRange(chart, this.applied) : null
    const pinned = !prev || prev.to >= this.applied - 1
    this.applied = n
    this.formingTs = null
    chart.applyNewData(this.display(n), this.forwardMore)
    if (n > 0) {
      this.offsetDirty = true
      if (frame === 'edge' || pinned || !prev || prev.to > n - 1) {
        setIndexRange(chart, { from: Math.max(0, n - 2), to: n - 1 }, n, rightPadBars, { keepZoom: true })
      } else {
        setIndexRange(chart, prev, n, 0, { keepZoom: true })
      }
    }
    this.emit({ type: 'data' })
  }

  /**
   * Lift the cutoff: everything buffered goes to the engine. A view pinned to the replay's edge
   * keeps its last revealed bar on the same pixel, and the buffered bars fill the margin it held.
   */
  private lift() {
    const chart = this.chart
    const m = this.applied
    const prev = m > 0 ? closedIndexRange(chart, m) : null
    const pinned = !prev || prev.to >= m - 1
    // Bars of blank space right of the last revealed bar. Readable exactly while it is
    // non-negative, which is what "pinned" means here (the engine clamps the getter at 0).
    const barSpace = chart.getBarSpace()
    const marginBars = barSpace > 0 ? chart.getOffsetRightDistance() / barSpace : 0
    this.applied = this.raw.length
    this.formingTs = null
    this.lastFormingRaw = null
    // Always a re-apply, never an append: the engine's clear() is also what re-arms right-side
    // paging (_backwardMore), which the cutoff had switched off.
    chart.applyNewData(this.display(this.applied), this.forwardMore)
    if (this.applied > 0 && m > 0) {
      this.offsetDirty = true
      if (pinned) {
        chart.setOffsetRightDistance((marginBars - (this.applied - m)) * barSpace)
      } else if (prev) {
        setIndexRange(chart, prev, this.applied, 0, { keepZoom: true })
      }
    }
    this.emit({ type: 'data' })
  }

  /** Append fetched bars to the buffer — and, with no cutoff, to the engine. */
  private appendRaw(fresh: KLineData[]) {
    const cut = this.cutoff !== null
    this.setRaw([...this.raw, ...fresh])
    if (!cut) {
      const chart = this.chart
      for (let i = this.applied; i < this.raw.length; i++) {
        if (this.ha) {
          const list = chart.getDataList()
          chart.updateData(nextHeikinAshi(list[i - 1], this.raw[i]))
        } else {
          chart.updateData(this.raw[i])
        }
      }
      this.applied = this.raw.length
    }
    this.emit({ type: 'data' })
  }

  /**
   * The bars after the last loaded one, up to the anchor. Widens across empty pages (a weekend,
   * a holiday, a data gap); an empty answer that reaches the anchor means there is nothing newer.
   */
  private async fetchAfter(): Promise<KLineData[]> {
    const socket = this.socket
    if (!socket || this.raw.length === 0) return []
    const lastMs = this.raw[this.raw.length - 1].timestamp
    const lastSec = Math.floor(lastMs / 1000)
    const limit = this.limitSec()
    let span = PAGE_BARS * getTimeframeSeconds(this.timeframe) * GAP_SLACK
    for (let i = 0; i < RIGHT_PROBES; i++) {
      const from = lastSec + 1
      const to = Math.min(limit, lastSec + span)
      if (to <= from) return []
      const rows = await socket.requestHistory({ symbol: this.symbol, timeframe: this.timeframe, from, to })
      // The datafeed answers from the bar CONTAINING `from` — i.e. the last bar again.
      const fresh = toKline(rows).filter((b) => b.timestamp > lastMs)
      if (fresh.length > 0) return fresh
      if (to >= limit) return []
      span *= 4
    }
    return []
  }

  /**
   * Before a live-edge applyNewData: put the canvas's own right margin back. The engine
   * re-applies the last offset anyone wrote (resetOffsetRightDistance), and the replay writes
   * negative ones — so without this the first load after a replay opens scrolled back.
   */
  private resetHomeOffset() {
    if (!this.offsetDirty) return
    this.offsetDirty = false
    this.chart.setOffsetRightDistance(this.homeOffsetPx)
  }
}
